import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/LanguageContext";
import { finalizeBridgeIfPending } from "@/lib/authBridge";
import ClientAuthDialog from "@/components/auth/ClientAuthDialog";

/**
 * useRequireAuth — the transactional-action gate shared by every public
 * booking/checkout/enroll entry point (Reserver, Consultations, Boutique,
 * Programmes, Parcours, LearnPlayer). See
 * docs/auth-and-admin/client-otp-auth-contract.md.
 *
 * `ensureAuthed(typedEmail?)` resolves the CURRENT user immediately when a session
 * already exists; otherwise it opens the shared dialog and resolves once the visitor
 * signs in, or `null` if they dismiss it. Each page renders `{authDialog}` once.
 *
 * IT ALSO OWNS WHAT THE VISITOR IS TOLD (BUG-052), because it is the one thing all six
 * purchase surfaces share. Every one of them wrote the same two lines —
 * `const u = await ensureAuthed(); if (!u) return;` — so dismissing the dialog left the
 * page looking exactly as it had, with no purchase and no explanation. Six identical
 * silent aborts, and six chances for a per-page message to drift.
 *
 * `typedEmail` is optional, and when it is supplied AND the visitor signs in with a
 * different address, `ensureAuthed` deliberately resolves `null` — the purchase does not
 * proceed on that press. A purchase is always booked against the signed-in identity,
 * never against a typed address the buyer has not proved (`Reserver.checkoutEmail`); the
 * substitution is correct, and being told about it only after the fact is the defect. A
 * toast alone does not fix that: the pay path navigates the whole page to Stripe the
 * instant it resolves, so the message and the page it sits on disappear together. So the
 * FIRST press signs them in and states which address will be used, the SECOND — with the
 * form field now visibly showing that address, locked — makes the purchase.
 *
 * Callers need no change for this: every one of them already reads `if (!u) return;`.
 * Pages with no email field pass nothing, never take this branch, and are unaffected.
 *
 * Also finalizes a pending Google auth-bridge round trip on mount: a bridge
 * sign-in started from THIS dialog on a tenant host (lib/authBridge.js —
 * GIS only trusts the platform origin) leaves via a full page navigation and
 * returns here with `?hb_finalize=1`. public/ClientPortal.jsx has its own copy
 * of this same finalize step for its standalone login card; every OTHER page
 * that can offer Google sign-in does so only through this hook, so the
 * finalize has to live here too rather than being duplicated per page.
 *
 * BUG-195 — `ensureAuthed`'s optional second argument, `onBeforeGoogleBridge`, is what
 * lets a caller survive that same full-page navigation. The OTP path and the
 * platform-host Google button never leave the page (a dialog, or a popup respectively),
 * so they never lose anything the caller does not already hold in memory. The
 * TENANT-host bridge is the one path that unmounts the caller before it can resolve
 * `ensureAuthed`'s promise — `onBeforeGoogleBridge(nonce)`, if supplied, fires at the one
 * instant before that happens, carrying the bridge's own single-use nonce (see
 * lib/authBridge.js's `beginBridgeSignin`). This hook stays agnostic to WHAT is persisted
 * — it only guarantees WHEN the callback runs and WHICH nonce this attempt used.
 *
 * `bridgeNonce`, in the return value, is the other half of that same contract: `undefined`
 * until the mount-time bridge check has resolved, `null` once resolved if this mount is
 * NOT a return from the bridge (no `hb_finalize`/`auth_error` was in the URL — an ordinary
 * page load, first-time or repeat visitor alike), or the nonce string that
 * `finalizeBridgeIfPending` read out of sessionStorage when it IS. A caller with state to
 * restore must gate that restore on `bridgeNonce` being that SAME nonce (never on its own
 * presence alone, and never unconditionally) — see lib/pendingBookingDraft.js's read side.
 * This is deliberately the one thing this hook DOES know about the draft mechanism: not its
 * shape, only the secret that must unlock it.
 *
 * PREFILL, opt-in. `ensureAuthed(typedEmail, onBeforeGoogleBridge, { prefill: true })`
 * opens the dialog with `typedEmail` already in its address field, for a page whose own
 * form has just collected that address (the pathway join form): the visitor is not asked
 * to type it twice. The field stays editable, and the mismatch rule above is unchanged:
 * signing in as another address still stops that press. Callers that do not pass the
 * option get the empty field they always had.
 */
export function useRequireAuth() {
  const { user, loading, refresh } = useAuth();
  const t = useT("public");
  const [open, setOpen] = useState(false);
  const resolveRef = useRef(null);
  // The address the page had already collected when the gate opened, so `handleSuccess`
  // can compare it with the identity that actually signed in. Held in a ref rather than
  // state: it is read once, inside a callback, and must not re-render the dialog.
  const typedEmailRef = useRef("");
  // BUG-195 — the caller's own "about to lose this to a full-page redirect" hook, set
  // fresh on every `ensureAuthed` call and read exactly once, from the bridge button
  // itself (see `handleBeforeGoogleBridge` below).
  const onBeforeGoogleBridgeRef = useRef(null);
  // React StrictMode double-invokes mount effects in dev; the bridge's hb_code
  // cookie is single-use server-side, so a second finalize call in the same
  // mount would race the first with the nonce already cleared and fail.
  const bridgeCheckedRef = useRef(false);
  // BUG-195 — see the docstring above. `undefined` = not checked yet, `null` = checked,
  // not a bridge return, string = the nonce THIS mount's bridge round trip used.
  const [bridgeNonce, setBridgeNonce] = useState(undefined);
  // The address the dialog opens with ("" unless the caller asked for a prefill). State,
  // not a ref: it is rendered into the dialog.
  const [initialEmail, setInitialEmail] = useState("");

  useEffect(() => {
    if (bridgeCheckedRef.current) return;
    bridgeCheckedRef.current = true;
    finalizeBridgeIfPending(refresh).then((result) => {
      setBridgeNonce(result.status === "none" ? null : (result.nonce || null));
    });
  }, [refresh]);

  const ensureAuthed = useCallback((typedEmail = "", onBeforeGoogleBridge = null, { prefill = false } = {}) => {
    // Already signed in: nothing was interrupted, so nothing is said. A message here
    // would fire on every single purchase.
    if (!loading && user) return Promise.resolve(user);
    typedEmailRef.current = String(typedEmail || "").trim();
    setInitialEmail(prefill ? typedEmailRef.current : "");
    onBeforeGoogleBridgeRef.current = onBeforeGoogleBridge || null;
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setOpen(true);
    });
  }, [user, loading]);

  // BUG-195 — invoked by ClientAuthPanel/authBridge.js's `onNonceReady`, the instant
  // before `beginBridgeSignin` navigates the whole page away (never reached by the OTP
  // path or the platform-host Google popup). Whatever the caller registered gets its one
  // chance to run here, carrying the nonce this specific bridge attempt just minted;
  // there is nothing to reset afterwards because the page is about to unmount.
  const handleBeforeGoogleBridge = useCallback((nonce) => {
    onBeforeGoogleBridgeRef.current?.(nonce);
  }, []);

  const handleSuccess = useCallback((u) => {
    setOpen(false);
    const typed = typedEmailRef.current;
    typedEmailRef.current = "";
    const resolve = resolveRef.current;
    resolveRef.current = null;
    // WHICH ADDRESS THE PURCHASE WILL USE, said at the moment it is decided — and the
    // purchase held back one press so the message is readable. Resolving the user here
    // would send the page to Stripe immediately, taking the notice with it.
    const signedIn = String(u?.email || "").trim();
    if (typed && signedIn && typed.toLowerCase() !== signedIn.toLowerCase()) {
      toast.info(t("clientAuth.emailReplaced", { email: signedIn }));
      resolve?.(null);
      return;
    }
    resolve?.(u);
  }, [t]);

  const handleOpenChange = useCallback((next) => {
    setOpen(next);
    if (!next) {
      const resolve = resolveRef.current;
      resolveRef.current = null;
      typedEmailRef.current = "";
      // ONLY when a caller was actually waiting. `open` also goes false on a successful
      // sign-in (handleSuccess closes the dialog first), and a "you cancelled" message
      // after a completed sign-in would be a plain lie; the null resolver is what tells
      // the two apart.
      if (resolve) toast.info(t("clientAuth.dismissed"));
      resolve?.(null);
    }
  }, [t]);

  const authDialog = (
    <ClientAuthDialog
      open={open}
      onOpenChange={handleOpenChange}
      onSuccess={handleSuccess}
      onBeforeGoogleBridge={handleBeforeGoogleBridge}
      initialEmail={initialEmail}
    />
  );

  return { user, loading, ensureAuthed, authDialog, bridgeNonce };
}

export default useRequireAuth;
